import { NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterModule,
  Routes,
} from '@angular/router';
import { RouterStateSerializer } from '@ngrx/router-store';
import { OktaAuthGuard, OktaCallbackComponent } from '@okta/okta-angular';

/* Components */
import * as fromBillingContainers from 'src/app/billing/containers';
import { CellularUsagePageComponent } from 'src/app/cellular-usage/containers';
import * as fromServiceInventoryComponents from 'src/app/inventory/components';
import * as fromServiceInventoryContainers from 'src/app/inventory/containers';
import { OrdersShellComponent } from 'src/app/orders/containers/orders-shell/orders-shell.component';
import { OverallNetworkStatusPageComponent } from 'src/app/overall-network-status/containers';
import * as fromGuards from 'src/app/store/guards';
import * as fromTicketContainers from 'src/app/tickets/containers';
import * as UnusualNetworkMetricsContainers from 'src/app/unusual-network-metrics/containers';
import { AppLayoutComponent } from './_layout/app-layout/app-layout.component';
import { PublicLayoutComponent } from './_layout/public-layout/public-layout.component';
import { AccessDeniedComponent } from './core/access-denied/access-denied.component';
import { AccessUnauthorizedComponent } from './core/access-unauthorized/access-unauthorized.component';
import { ForgotPasswordComponent } from './core/forgot-password/forgot-password.component';
import { ImpersonationComponent } from './core/impersonate/impersonation.component';
import { LoginComponent } from './core/login/login.component';
import { LogoutComponent } from './core/logout/logout.component';
import { PageNotFoundComponent } from './core/page-not-found/page-not-found.component';
import { ResetPasswordComponent } from './core/reset-password/reset-password.component';
import { DashboardComponent } from './dashboard/dashboard-page/dashboard.component';
import { ManageUsersComponent } from './manage-users/manage-users.component';
import { OnboardingOktaUserComponent } from './manage-users/onboarding-okta-user/onboarding-okta-user.component';
import { MetricTableComponent } from './metric-details/containers';
import { NocPolicyComponent } from './noc-policy/noc-policy.component';
import { OperationsComponent } from './operations-dashboard/operations-dashboard/operations-page.component';
import * as fromOrdersTableComponents from './orders-table';
import { QoePageComponent } from './quality-of-experience/containers';
import { CustomSerializer } from './store/reducers/router.reducer';
import { SummaryComponent } from './summary/summary-page/summary-page.component';

import { Permission } from './shared/constants/permissions';

const ROUTES: Routes = [
  {
    path: '',
    component: AppLayoutComponent,
    children: [
      {
        path: 'overall-network-status',
        redirectTo: 'analytics/overall-network-status',
        pathMatch: 'full',
      },
      {
        path: 'analytics/overall-network-status',
        component: OverallNetworkStatusPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.InvoiceAttachmentsGuard,
          fromGuards.PaymentsGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'cellular-usage',
        redirectTo: 'analytics/cellular-usage',
        pathMatch: 'full',
      },
      {
        path: 'analytics/cellular-usage',
        component: CellularUsagePageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.InvoiceAttachmentsGuard,
          fromGuards.PaymentsGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'unusual-network-metrics',
        redirectTo: 'analytics/unusual-network-metrics',
        pathMatch: 'full',
      },
      {
        path: 'analytics/unusual-network-metrics',
        component: UnusualNetworkMetricsContainers.UnmPageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.InvoiceAttachmentsGuard,
          fromGuards.PaymentsGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'billing',
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.InvoiceAttachmentsGuard,
          fromGuards.PaymentsGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.BILLING_READ],
          }),
        ],
        children: [
          { path: '', component: fromBillingContainers.BillingPageComponent },
        ],
      },
      {
        path: 'metric-details',
        redirectTo: 'analytics/metric-details',
        pathMatch: 'full',
      },
      {
        path: 'analytics/metric-details',
        component: MetricTableComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'quality-of-experience',
        redirectTo: 'analytics/quality-of-experience',
        pathMatch: 'full',
      },
      {
        path: 'quality-of-experience-aggregate',
        redirectTo: 'analytics/quality-of-experience',
        pathMatch: 'full',
      },
      {
        path: 'analytics/quality-of-experience',
        component: QoePageComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: 'services/:serviceId',
        component: fromServiceInventoryComponents.InventoryDetailComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.ServicesGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.SERVICES],
          }),
        ],
      },
      {
        path: 'services',
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.SERVICES],
          }),
        ],
        children: [
          {
            path: '',
            component: fromServiceInventoryContainers.InventoryPageComponent,
          },
        ],
      },
      {
        path: 'orders/:orderId/line-items/:lineItemId',
        component: OrdersShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.OrdersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders/:orderId',
        component: OrdersShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.OrdersGuard,
          fromGuards.SOFGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders',
        component: OrdersShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders-table',
        component: fromOrdersTableComponents.OrdersIndex,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'orders-table/:orderId/view',
        component: fromOrdersTableComponents.OrdersIndex,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'tickets/:ticketId/comments',
        component: fromTicketContainers.TicketCommentComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.TicketsGuard,
          fromGuards.MobileOnly.onRejected((route) => {
            const { ticketId } = route.params;
            return {
              commands: ['/tickets'],
              extras: { queryParams: { ticket: ticketId } },
            };
          }),
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.TICKETING_READ],
          }),
        ] as CanActivateFn[],
      },
      {
        path: 'tickets/:ticketId',
        component: fromTicketContainers.TicketsDetailShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.TicketsGuard,
          fromGuards.MobileOnly.onRejected((route) => {
            const { ticketId } = route.params;
            return {
              commands: ['/tickets'],
              extras: { queryParams: { ticket: ticketId } },
            };
          }),
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.TICKETING_READ],
          }),
        ] as CanActivateFn[],
      },
      {
        path: 'tickets',
        component: fromTicketContainers.TicketsShellComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.TICKETING_READ],
          }),
        ],
      },
      {
        path: 'summary',
        redirectTo: 'reports/orders-summary',
        pathMatch: 'full',
      },
      {
        path: 'orders-dashboard',
        redirectTo: 'repors/orders-summary',
        pathMatch: 'full',
      },
      {
        path: 'reports/orders-summary',
        component: SummaryComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.ORDERS],
          }),
        ],
      },
      {
        path: 'operations-summary',
        redirectTo: 'repors/operations-summary',
        pathMatch: 'full',
      },
      {
        path: 'reports/operations-summary',
        component: OperationsComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [
              Permission.TICKETING_READ,
              Permission.SERVICES,
              Permission.ORDERS,
            ],
          }),
        ],
      },
      {
        path: 'analytics/historical-observations',
        loadChildren: () =>
          import('./tickets-historical-observations/tickets-historical-observations.module').then(
            (m) => m.TicketsHistoricalObservationsModule,
          ),
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_IQ_PARTICIPANT],
          }),
        ],
      },
      {
        path: '',
        component: DashboardComponent,
        canActivate: [fromGuards.DynamicAuthGuard, fromGuards.UsersGuard],
      },
      {
        path: 'noc-policy',
        component: NocPolicyComponent,
        canActivate: [fromGuards.DynamicAuthGuard],
      },
      {
        path: 'manage/account',
        component: ManageUsersComponent,
        canActivate: [OktaAuthGuard, fromGuards.UsersGuard],
      },
      {
        path: 'onboard',
        component: OnboardingOktaUserComponent,
        canActivate: [
          fromGuards.DynamicAuthGuard,
          fromGuards.UsersGuard,
          fromGuards.PermissionsGuard.withRedirect({
            every: [Permission.NITEL_INTERNAL],
          }),
        ],
      },
      {
        path: 'access-denied',
        component: AccessDeniedComponent,
        canActivate: [OktaAuthGuard],
      },
      { path: 'unauthorized', component: AccessUnauthorizedComponent },
    ],
  },
  {
    // Logged out routes go here
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: 'login/callback', component: OktaCallbackComponent },
      { path: 'page-not-found', component: PageNotFoundComponent },
      { path: 'logout', component: LogoutComponent },
      { path: 'login', component: LoginComponent },
      { path: 'forgot-password', component: ForgotPasswordComponent },
      { path: 'reset-password', component: ResetPasswordComponent },
      { path: 'signin_as', component: ImpersonationComponent },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
      { path: '**', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(ROUTES, {
      scrollPositionRestoration: 'enabled',
      anchorScrolling: 'enabled',
      scrollOffset: [0, 70],
      enableTracing: false,
    }),
  ],
  exports: [RouterModule],
  providers: [
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    {
      provide: 'externalUrlRedirectResolver',
      useValue: (route: ActivatedRouteSnapshot) => {
        window.location.href = (route.data as any).externalUrl;
      },
    },
  ],
})
export class AppRoutingModule {}
